import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@roger/ui-components';
import {
  Archive,
  ArchiveRestore,
  Loader2,
  Command,
  ChevronDown,
} from 'lucide-react';
import { bulkUpdateCodingTags } from '../services/rogerDocApi';

interface CodingActionsMenuProps {
  diagnosisIds: string[];
  onActionComplete?: () => void;
  trigger?: React.ReactNode;
}

export const CodingActionsMenu: React.FC<CodingActionsMenuProps> = ({
  diagnosisIds,
  onActionComplete,
  trigger,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'archive' | 'unarchive' | null>(
    null
  );
  const [loading, setLoading] = useState(false);

  const handleActionClick = (type: 'archive' | 'unarchive') => {
    setActionType(type);
    // Small delay to let dropdown close before opening dialog
    setTimeout(() => {
      setDialogOpen(true);
    }, 100);
  };

  const handleConfirm = async () => {
    if (!actionType || diagnosisIds.length === 0) return;

    setLoading(true);
    try {
      await bulkUpdateCodingTags({
        diagnosis_ids: diagnosisIds,
        tags: { archived: actionType === 'archive' },
      });
      setDialogOpen(false);
      if (onActionComplete) {
        onActionComplete();
      }
    } catch (error) {
      console.error(`Failed to ${actionType} visits:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setActionType(null);
  };

  const count = diagnosisIds.length;
  const isSingle = count === 1;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {trigger || (
            <button className='flex items-center gap-2 h-7 px-2 text-sm bg-black/20 border border-white/10 rounded hover:bg-black/30 transition-colors'>
              <Command className='w-3.5 h-3.5' />
              Actions
              <ChevronDown className='w-3.5 h-3.5' />
            </button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem onClick={() => handleActionClick('archive')}>
            <Archive className='w-4 h-4 mr-2 text-orange-600' />
            Archive
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleActionClick('unarchive')}>
            <ArchiveRestore className='w-4 h-4 mr-2 text-green-600' />
            Unarchive
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={dialogOpen}
        onOpenChange={open => {
          if (!open) handleClose();
        }}
      >
        <DialogContent
          onCloseAutoFocus={e => e.preventDefault()}
          onOpenAutoFocus={e => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>
              {actionType === 'archive' ? 'Archive' : 'Unarchive'}{' '}
              {isSingle ? 'Visit' : 'Visits'}
            </DialogTitle>
          </DialogHeader>

          <div className='py-4'>
            <p className='text-gray-600'>
              Are you sure you want to{' '}
              {actionType === 'archive' ? 'archive' : 'unarchive'}{' '}
              {isSingle ? (
                'this visit'
              ) : (
                <>
                  <strong>{count}</strong> selected visits
                </>
              )}
              ?
            </p>
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={handleClose} disabled={loading}>
              No
            </Button>
            <Button onClick={handleConfirm} disabled={loading}>
              {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Yes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
